import { ApolloError } from '@apollo/client';
import { noop } from 'lodash';
import { type ReactNode, useMemo } from 'react';

import { groupSuggestionsByEntityOrTestamentaryEntityID } from '@/modules/aiSuggestions/aiSuggestions.utils';
import {
  EstateHoldingsContext,
  EstatePageSuggestionSummary,
} from '@/modules/estate/contexts/estateHoldings.context';
import {
  EstateOnboardingUnacknowledgedSuggestionsContext,
  EstateOnboardingUnacknowledgedSuggestionsContextShape,
} from '@/modules/estate/estateAIOnboarding/context/estateOnboardingUnacknowledgedSuggestions.context';
import { EstateOnboardingSuggestionGroup } from '@/modules/estate/estateAIOnboarding/estateOnboarding.types';
import { EMPTY_GROUPS } from '@/modules/estate/estateAIOnboarding/estateOnboarding.utils';
import {
  EstatePage_EntityFragment,
  EstatePage_TestamentaryEntityFragment,
} from '@/modules/estate/graphql/EstatePageData.generated';

interface ReviewSuggestionProviderProps {
  children: ReactNode;
  entities: EstatePage_EntityFragment[];
  error?: ApolloError;
  loading: boolean;
  refetch: () => void;
  suggestions: EstatePageSuggestionSummary[];
  testamentaryEntities?: EstatePage_TestamentaryEntityFragment[];
}

export function ReviewSuggestionProvider({
  children,
  entities,
  error,
  loading,
  refetch,
  suggestions,
  testamentaryEntities = [],
}: ReviewSuggestionProviderProps) {
  const suggestionsByEntityID = useMemo(
    () => groupSuggestionsByEntityOrTestamentaryEntityID(suggestions),
    [suggestions]
  );
  const estateHoldingsValue = useMemo<EstateHoldingsContext>(
    () => ({
      loading,
      setLoading: noop,
      entities,
      setEntities: noop,
      testamentaryEntities,
      setTestamentaryEntities: noop,
      draftEntities: [],
      setDraftEntities: noop,
      unacknowledgedSuggestionsV2: suggestionsByEntityID,
      setUnacknowledgedSuggestionsV2: noop,
      error: error ?? null,
      setError: noop,
      refetch,
      setRefetch: noop,
    }),
    [
      entities,
      error,
      loading,
      refetch,
      suggestionsByEntityID,
      testamentaryEntities,
    ]
  );
  const onboardingValue =
    useMemo<EstateOnboardingUnacknowledgedSuggestionsContextShape>(
      () => ({
        suggestionsToReview: [],
        suggestionsToReviewByGroup: EMPTY_GROUPS,
        additionalReferenceSuggestions: [],
        countsByGroup: {
          [EstateOnboardingSuggestionGroup.DirectHoldings]: 0,
          [EstateOnboardingSuggestionGroup.TrustEntities]: 0,
          [EstateOnboardingSuggestionGroup.TrustHoldings]: 0,
          [EstateOnboardingSuggestionGroup.BusinessEntities]: 0,
          [EstateOnboardingSuggestionGroup.BusinessHoldings]: 0,
        },
        totalCount: 0,
        loading,
        refetch,
      }),
      [loading, refetch]
    );

  return (
    <EstateHoldingsContext.Provider value={estateHoldingsValue}>
      <EstateOnboardingUnacknowledgedSuggestionsContext.Provider
        value={onboardingValue}
      >
        {children}
      </EstateOnboardingUnacknowledgedSuggestionsContext.Provider>
    </EstateHoldingsContext.Provider>
  );
}
